import { AsyncPipe, NgClass } from '@angular/common';
import { Component, computed, inject, input, output, signal } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { LanguagePipe } from '../../pipes/language.pipe';
import { ImageReader } from '../../pipes/image-reader';
import { UserService } from '../../services/user';
import { ButtonModule } from 'primeng/button';

@Component({
  selector: 'lib-user-profile',
  standalone: true,
  imports: [
    NgClass,
    TranslocoPipe,
    LanguagePipe,
    AsyncPipe,
    ImageReader,
    ButtonModule,
  ],
  templateUrl: './user-profile.html',
  styleUrl: './user-profile.css',
})
export class UserProfile {
  private readonly userService = inject(UserService);

  styleClass = input('');

  userData = input<any>();

  relationType = input<number | null>(null);

  /** Applicant photo update — only when parent passes a valid participant userId. */
  allowChangePhoto = input(false);

  targetUserId = input<number | null>(null);

  photoChanged = output<string>();

  readonly uploadingPhoto = signal(false);

  readonly photoSource = computed(
    () => this.userData()?.imageId || this.userData()?.imgUrl || '',
  );

  readonly canChangePhoto = computed(() => {
    const userId = this.targetUserId();
    const data = this.userData();
    if (data?.isPhotoGCP === true) {
      return false;
    }
    return this.allowChangePhoto() && typeof userId === 'number' && userId > 0;
  });

  showUserData: {
    label: string;
    value: string;
  }[] = [
    {
      label: 'birthday',
      value: 'birthDate',
    },
    {
      label: 'nationality',
      value: 'nationality',
    },
    {
      label: 'passportData',
      value: 'passportInfo',
    },
    {
      label: 'phoneNumber',
      value: 'phoneNumber',
    },
    {
      label: 'pinfl',
      value: 'pinfl',
    },
  ];

  getGenderText(gender: number | string): string {
    if (typeof gender === 'number') {
      return gender === 1 ? 'man' : 'girl';
    }
    return gender || 'Not specified';
  }

  getDisplayValue(value: any, field: string): string {
    const data = this.userData();
    if (
      field === 'passportInfo' &&
      (data?.documentSerial || data?.documentNumber)
    ) {
      return `${data?.documentSerial ?? ''}${data?.documentNumber ?? ''}`;
    }

    if (value === null || value === undefined || value === '') {
      return '-';
    }

    if (field === 'gender') {
      return this.getGenderText(value);
    }

    return String(value);
  }

  getRelationTypeText(type: number): string {
    switch (type) {
      case 1:
        return 'father';
      case 2:
        return 'mother';
      case 3:
        return 'guardian';
      default:
        return 'relative';
    }
  }

  getValue(path: string): any {
    return path
      .split('.')
      .reduce<any>((obj, key) => obj?.[key], this.userData());
  }

  onChangePhoto(event: Event): void {
    const userId = this.targetUserId();
    if (!this.canChangePhoto() || userId == null || this.uploadingPhoto()) {
      return;
    }

    const request$ = this.userService.changeUserPhoto(event, userId);
    if (!request$) {
      return;
    }

    this.uploadingPhoto.set(true);
    request$.subscribe({
      next: (res) => {
        this.uploadingPhoto.set(false);
        if (res?.result) {
          this.photoChanged.emit(res.result);
        }
      },
      error: () => {
        this.uploadingPhoto.set(false);
      },
    });
  }
}
